import { useCallback, useMemo } from 'react'
import { SLIGHTLY_OVERUTILIZED_THRESHOLD } from '@constants'
import { filter, groupBy, maxBy, reduce, reject } from 'lodash-es'
import { useTheme } from '@mui/material'
import { useSceneStore } from '@editor/stores'
import {
  useControlStore,
  useTransmissionStore,
  useElementLoadStore,
  useSystemManagerStore,
  useResultsStore,
} from '@editorStores'
import useElementTypes from './useElementTypes'
import useGuidToDomain from './useGuidToDomain'
import useGuidToElement from './useGuidToElement'
import useLoadProblems from './useLoadProblems'
import usePositionGuidToWallGuid from './usePositionGuidToWallGuid'

const useColoringWidgetMapping = (): Record<string, string> | null => {
  const theme = useTheme()

  const coloringMode = useControlStore(state => state.coloringMode)
  const copyRipsOriginWallGuid = useControlStore(state => state.copyRipsOriginWallGuid)
  const manualGuidToColor = useSceneStore(state => state.manualGuidToColor)

  const problemViewActiveErrors = useControlStore(state => state.problemViewActiveErrors)
  const problemViewActiveWarnings = useControlStore(state => state.problemViewActiveWarnings)

  const verticalTransmissionGraph = useTransmissionStore(state => state.verticalTransmissionGraph)
  const loadsPerElement = useElementLoadStore(state => state.loads)

  const elementTypes = useElementTypes()

  const positionGuidToWallGuid = usePositionGuidToWallGuid()

  const guidToDomain = useGuidToDomain()
  const guidToElement = useGuidToElement() as Record<string, ShapeObject>

  const selectedAssemblies = useControlStore(state => state.selectedAssembliesForColoring)
  const assemblyAssignment = useSystemManagerStore(state => state.assemblyAssignment)

  // PROBLEMS

  const problems = useLoadProblems(
    verticalTransmissionGraph,
    elementTypes,
    positionGuidToWallGuid,
    guidToDomain,
    loadsPerElement,
    guidToElement,
  )

  const problematicElements = useMemo(() => {
    const filteredProblems = filter(problems?.problemsOnElements, problem => {
      if (problem.severity === 'error' && problemViewActiveErrors) return true
      if (problem.severity === 'warning' && problemViewActiveWarnings) return true
      return false
    })

    return reduce(
      filteredProblems,
      (collector, problem) => {
        const nextColor = (
          problem.severity === 'error' ? theme.scenePalette.invalid : theme.scenePalette.warning
        ) as string
        const hasError = collector[problem.element_guid] === theme.scenePalette.invalid
        // Keep error color if warning is processed later.
        if (hasError && problem.severity !== 'error') return collector
        collector[problem.element_guid] = nextColor

        return collector
      },
      {} as Record<string, string>,
    )
  }, [
    problems?.problemsOnElements,
    problemViewActiveErrors,
    problemViewActiveWarnings,
    theme.scenePalette.invalid,
    theme.scenePalette.warning,
  ])

  // ASSEMBLIES

  const elementsWithinSelectedAssemblies = useMemo(
    () =>
      reduce(
        assemblyAssignment,
        (col, assignment) => {
          if (selectedAssemblies.has(assignment.assembly_guid)) {
            col[assignment.element_guid] = theme.scenePalette.highlightDark as string
          }
          return col
        },
        {} as Record<string, string>,
      ),
    [assemblyAssignment, selectedAssemblies, theme.scenePalette.highlightDark],
  )

  // GROUPED ELEMENTS

  const wallRipsPositionGrouping = useResultsStore(state => state.wallRipsPositionGrouping)
  const wallLintelsPositionGrouping = useResultsStore(state => state.wallLintelsPositionGrouping)
  const beamsPositionGrouping = useResultsStore(state => state.beamsPositionGrouping)
  const columnsPositionGrouping = useResultsStore(state => state.columnsPositionGrouping)
  const purlinsPositionGrouping = useResultsStore(state => state.purlinsPositionGrouping)
  const slabBeamsPositionGrouping = useResultsStore(state => state.slabBeamsPositionGrouping)
  const roofSlabBeamsPositionGrouping = useResultsStore(
    state => state.roofSlabBeamsPositionGrouping,
  )
  const structuralChecks = useResultsStore(state => state.structuralChecks)

  // UTILIZATION η (same logic as utilization column) and ηc,90 (same as utilization90 column)
  // Colored per individual element, not per group/bundle — a bundled element's own checks
  // determine its color, independent of its siblings' or the representative's utilization.

  const groupedElementGuids = useMemo(() => {
    const bundles = [
      ...(wallRipsPositionGrouping ?? []),
      ...(wallLintelsPositionGrouping ?? []),
      ...(beamsPositionGrouping ?? []),
      ...(columnsPositionGrouping ?? []),
      ...(purlinsPositionGrouping ?? []),
      ...(slabBeamsPositionGrouping ?? []),
      ...(roofSlabBeamsPositionGrouping ?? []),
    ]
    return bundles.flatMap(bundle => [bundle.representative_position, ...bundle.representative_for])
  }, [
    wallRipsPositionGrouping,
    wallLintelsPositionGrouping,
    beamsPositionGrouping,
    columnsPositionGrouping,
    purlinsPositionGrouping,
    slabBeamsPositionGrouping,
    roofSlabBeamsPositionGrouping,
  ])

  const checksByElementGuid = useMemo(
    () => (structuralChecks ? groupBy(structuralChecks, 'element_guid') : {}),
    [structuralChecks],
  )

  const utilizationMapping = useMemo(() => {
    if (!structuralChecks || structuralChecks.length === 0) return {}
    const over = theme.results.utilization.overutilized.main as string
    const slight = theme.results.utilization.slightlyOverutilized.main as string
    const under = theme.results.utilization.underutilized.main as string
    return reduce(
      groupedElementGuids,
      (col, guid) => {
        const elementChecksWithoutSupportCompression = reject(
          checksByElementGuid[guid],
          check =>
            check.check_type === 'SupportCompression' ||
            check.check_type === 'SteelSupportCompression',
        )
        const check = maxBy(elementChecksWithoutSupportCompression, check => check.max_utilization)
        const maxUtilization = check?.max_utilization ?? 0
        const isVibrationError = elementChecksWithoutSupportCompression.some(
          c => c.check_type === 'Vibration' && c.max_utilization === 1.0,
        )
        let color = under
        if (isVibrationError || maxUtilization >= SLIGHTLY_OVERUTILIZED_THRESHOLD) {
          color = over
        } else if (maxUtilization >= 1.0 && maxUtilization < SLIGHTLY_OVERUTILIZED_THRESHOLD) {
          color = slight
        }
        col[guid] = color
        return col
      },
      {} as Record<string, string>,
    )
  }, [
    structuralChecks,
    groupedElementGuids,
    checksByElementGuid,
    theme.results.utilization.overutilized.main,
    theme.results.utilization.slightlyOverutilized.main,
    theme.results.utilization.underutilized.main,
  ])

  const utilization90Mapping = useMemo(() => {
    if (!structuralChecks || structuralChecks.length === 0) return {}
    const over = theme.results.utilization.overutilized.main as string
    const slight = theme.results.utilization.slightlyOverutilized.main as string
    const under = theme.results.utilization.underutilized.main as string
    return reduce(
      groupedElementGuids,
      (col, guid) => {
        const elementChecksOnlySupportCompression = filter(
          checksByElementGuid[guid],
          check =>
            check.check_type === 'SupportCompression' ||
            check.check_type === 'SteelSupportCompression',
        )
        const check = maxBy(elementChecksOnlySupportCompression, check => check.max_utilization)
        const maxUtilization = check?.max_utilization ?? 0
        let color = under
        if (maxUtilization >= SLIGHTLY_OVERUTILIZED_THRESHOLD) {
          color = over
        } else if (maxUtilization >= 1.0 && maxUtilization < SLIGHTLY_OVERUTILIZED_THRESHOLD) {
          color = slight
        }
        col[guid] = color
        return col
      },
      {} as Record<string, string>,
    )
  }, [
    structuralChecks,
    groupedElementGuids,
    checksByElementGuid,
    theme.results.utilization.overutilized.main,
    theme.results.utilization.slightlyOverutilized.main,
    theme.results.utilization.underutilized.main,
  ])

  const groupingToMapping = useCallback(
    (bundles: MemberPositionBundle[]) =>
      reduce(
        bundles.map(bundle => ({
          [bundle.representative_position]: bundle.exported
            ? (theme.scenePalette.representatives.representative as string)
            : (undefined as unknown as string),
          ...reduce(
            bundle.representative_for,
            (collector, guid) => ({
              ...collector,
              [guid]: (bundle.exported
                ? theme.scenePalette.representatives.representativeFor
                : undefined) as unknown as string,
            }),
            {} as Record<string, string>,
          ),
        })),
        (collector, bundle) => ({ ...collector, ...bundle }),
        {} as Record<string, string>,
      ),
    [
      theme.scenePalette.representatives.representative,
      theme.scenePalette.representatives.representativeFor,
    ],
  )

  const colorMapping = useMemo(() => {
    const wallRipsMapping = wallRipsPositionGrouping
      ? groupingToMapping(wallRipsPositionGrouping)
      : {}
    const wallLintelsMapping = wallLintelsPositionGrouping
      ? groupingToMapping(wallLintelsPositionGrouping)
      : {}
    const beamsMapping = beamsPositionGrouping ? groupingToMapping(beamsPositionGrouping) : {}
    const columnsMapping = columnsPositionGrouping ? groupingToMapping(columnsPositionGrouping) : {}
    const purlinsMapping = purlinsPositionGrouping ? groupingToMapping(purlinsPositionGrouping) : {}
    const slabBeamsMapping = slabBeamsPositionGrouping
      ? groupingToMapping(slabBeamsPositionGrouping)
      : {}
    const roofSlabBeamsMapping = roofSlabBeamsPositionGrouping
      ? groupingToMapping(roofSlabBeamsPositionGrouping)
      : {}

    return {
      ...wallRipsMapping,
      ...wallLintelsMapping,
      ...beamsMapping,
      ...columnsMapping,
      ...purlinsMapping,
      ...slabBeamsMapping,
      ...roofSlabBeamsMapping,
    }
  }, [
    wallRipsPositionGrouping,
    groupingToMapping,
    wallLintelsPositionGrouping,
    beamsPositionGrouping,
    columnsPositionGrouping,
    purlinsPositionGrouping,
    slabBeamsPositionGrouping,
    roofSlabBeamsPositionGrouping,
  ])

  const copyRipsOriginOverlay = useMemo(
    () =>
      copyRipsOriginWallGuid
        ? { [copyRipsOriginWallGuid]: theme.scenePalette.elements3d.targets as string }
        : {},
    [copyRipsOriginWallGuid, theme.scenePalette.elements3d.targets],
  )

  const mergeCopyRipsOrigin = useCallback(
    (mapping: Record<string, string> | null): Record<string, string> | null => {
      if (Object.keys(copyRipsOriginOverlay).length === 0) return mapping
      if (!mapping) return copyRipsOriginOverlay
      return { ...mapping, ...copyRipsOriginOverlay }
    },
    [copyRipsOriginOverlay],
  )

  if (coloringMode === 'coloringAssemblies')
    return mergeCopyRipsOrigin(elementsWithinSelectedAssemblies)
  if (coloringMode === 'problemView') return mergeCopyRipsOrigin(problematicElements)
  if (coloringMode === 'storey') {
    const storeyMapping = Object.keys(manualGuidToColor).length > 0 ? manualGuidToColor : null
    return mergeCopyRipsOrigin(storeyMapping)
  }
  if (coloringMode === 'groupedElements') return mergeCopyRipsOrigin(colorMapping)
  if (coloringMode === 'utilization') return mergeCopyRipsOrigin(utilizationMapping)
  if (coloringMode === 'utilization90') return mergeCopyRipsOrigin(utilization90Mapping)

  return mergeCopyRipsOrigin(null)
}

export default useColoringWidgetMapping
